// @flow
import axios from 'lib/defaultClient';

const SESSION_KEY = '__tracking_session__';

type TrackingSession = {
  sessionId: string,
  startedAt: string,
};

type PageInfo = {
  path: string,
  title: string,
  referrer: string,
};

type EventProperties = {
  [key: string]: any,
};

const generateUUID = (): string => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    // eslint-disable-next-line no-bitwise
    const r = (Math.random() * 16) | 0;
    // eslint-disable-next-line no-bitwise
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const getSessionStorage = () => {
  if (typeof sessionStorage === 'undefined') return null;
  return sessionStorage;
};

const ANON_KEY = '__anonymous_id__';

const getLocalStorage = () => {
  if (typeof localStorage === 'undefined') return null;
  return localStorage;
};

// 비로그인 사용자도 식별할 수 있도록 localStorage에 영구 익명 ID를 보관한다.
// 로그인 후에도 동일 anonymous_id를 보내 서버가 user_id와 연결(stitching)한다.
export const getAnonymousId = (): ?string => {
  const ls = getLocalStorage();
  if (!ls) return null;
  try {
    let id = ls.getItem(ANON_KEY);
    if (!id) {
      id = generateUUID();
      ls.setItem(ANON_KEY, id);
    }
    return id;
  } catch (e) {
    return null;
  }
};

const getSession = (): ?TrackingSession => {
  const ss = getSessionStorage();
  if (!ss) return null;
  try {
    const raw = ss.getItem(SESSION_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (e) {
    return null;
  }
};

const setSession = (session: TrackingSession): void => {
  const ss = getSessionStorage();
  if (!ss) return;
  ss.setItem(SESSION_KEY, JSON.stringify(session));
};

const getPageInfo = (location?: { pathname: string, search: string }): PageInfo => {
  if (typeof window === 'undefined') {
    return { path: '', title: '', referrer: '' };
  }
  const path = location
    ? location.pathname + location.search
    : window.location.pathname + window.location.search;
  return {
    path,
    title: document.title || '',
    referrer: document.referrer || '',
  };
};

export const getUtmParams = (search: string = ''): { utmSource: ?string, utmMedium: ?string, utmCampaign: ?string } => {
  if (typeof window === 'undefined' && !search) {
    return { utmSource: null, utmMedium: null, utmCampaign: null };
  }

  const query = search || (typeof window !== 'undefined' ? window.location.search : '');
  const params = new URLSearchParams(query.startsWith('?') ? query.slice(1) : query);

  return {
    utmSource: params.get('utm_source'),
    utmMedium: params.get('utm_medium'),
    utmCampaign: params.get('utm_campaign'),
  };
};

let pendingSessionPromise: ?Promise<?string> = null;

const createSession = async (sessionId: string, startedAt: string): Promise<boolean> => {
  try {
    const page = getPageInfo();
    const utmParams = getUtmParams();
    await axios.post('/tracking/sessions', {
      sessionId,
      anonymousId: getAnonymousId(),
      startedAt,
      entryPath: page.path,
      referrer: page.referrer,
      utmSource: utmParams.utmSource,
      utmMedium: utmParams.utmMedium,
      utmCampaign: utmParams.utmCampaign,
    });
    return true;
  } catch (e) {
    return false;
  }
};

export const ensureSession = async (): Promise<?string> => {
  const session = getSession();
  if (session) return session.sessionId;

  if (pendingSessionPromise) return pendingSessionPromise;

  pendingSessionPromise = (async () => {
    const sessionId = generateUUID();
    const startedAt = new Date().toISOString();

    const success = await createSession(sessionId, startedAt);
    if (success) {
      setSession({ sessionId, startedAt });
      return sessionId;
    }
    return null;
  })();

  try {
    return await pendingSessionPromise;
  } finally {
    pendingSessionPromise = null;
  }
};

// sendBeacon/fetch는 절대 URL이 필요하므로 axios 인스턴스의 baseURL과 동일하게 맞춘다.
const resolveTrackingUrl = (path: string): string => {
  const base = (axios.defaults && axios.defaults.baseURL) || '';
  if (!base || base === '/') return path;
  return base.replace(/\/+$/, '') + path;
};

// 네비게이션 중에도 손실되지 않도록 sendBeacon → fetch(keepalive) → axios 순으로 시도한다.
// CORS preflight를 피하기 위해 text/plain Blob으로 전송한다(인증 쿠키는 자동 포함).
const deliverEvent = (payload: Object): void => {
  const url = resolveTrackingUrl('/tracking/events');
  const body = JSON.stringify(payload);

  if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
    try {
      const blob = new Blob([body], { type: 'text/plain' });
      if (navigator.sendBeacon(url, blob)) return;
    } catch (e) {
      // fall through
    }
  }

  if (typeof fetch === 'function') {
    try {
      fetch(url, {
        method: 'POST',
        body,
        headers: { 'Content-Type': 'text/plain' },
        keepalive: true,
        credentials: 'include',
      }).catch(() => {});
      return;
    } catch (e) {
      // fall through
    }
  }

  axios.post('/tracking/events', payload).catch(() => {});
};

export const send = async (
  eventName: string,
  properties: EventProperties = {},
): Promise<void> => {
  const sessionId = await ensureSession();
  if (!sessionId) return;

  const page = getPageInfo();
  deliverEvent({
    eventId: generateUUID(),
    sessionId,
    anonymousId: getAnonymousId(),
    eventName,
    occurredAt: new Date().toISOString(),
    page,
    properties,
  });
};

export const reset = (): void => {
  const ss = getSessionStorage();
  if (!ss) return;
  ss.removeItem(SESSION_KEY);
  pendingSessionPromise = null;
};



// WEBPACK FOOTER //
// ./src/lib/tracking/dbTracker.js